/* eslint-disable max-depth */
import {
    type NodeCfg,
    type NodeControlSocketKindMap,
    type NodeDataSocketKindMap,
    type NodeSocketKind,
    type NodeSocketKindMap,
    type ProcessWorkInstruction,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { RequiredSockets, UiNodeSpec } from './UiNodeSpec';
import { gqlClient } from 'src/util/gql-client';
import { ArgoWorkInstructionVersionStatusEnum, GetProcessDataElementsForWorkInstructionInputsDocument, GetWorkInstructionVersionDetailDocument, ProcessDataElementCollectionKindEnum } from 'types/db';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { AsyncComponent } from 'vue';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import { fromZodError } from 'zod-validation-error';
import { Notify } from 'src/notifications';
import { CausalError } from '@redviking/causal-error';
import { workInstructionVersionSchema } from '@redviking/argonaut-util/types/work-instruction-version';
import type { UiFlowNodeOutput } from '../../sockets/UiFlowNodeOutput';

export const workInstructionUiNodeSpec: UiNodeSpec<'workInstruction', NodeCfg<'workInstruction'>, NodeSocketKindMap['workInstruction']> = {
    nodeCategory: 'argonautApi',
    kindLabel: 'Work Instruction',
    requiredSockets: node => {
        const ret: ReturnType<RequiredSockets<typeof node.kind, NodeSocketKindMap[typeof node.kind]>> = {
            required: [ 'activate' ],
            conditionallyRequired: [],
        };
        if (node.controlValues.workInstructionDetail.kind === 'process') {
            ret.required.push('materialId');
        }
        return ret;
    },
    extraComponent: cfg => {
        if (cfg.kind !== 'workInstruction') {
            return null;
        }
        const typedCfg = cfg as NodeCfg<'workInstruction'>;
        if (typedCfg.controlValues.workInstructionDetail.kind === 'process') {
            const comp: AsyncComponent = () => import('@redviking/argonaut-core-ui/src/applet/design/flows/inspector/WorkInstructionNode.helper.vue').then(m => m.default);
            return comp;
        }
        return null;
    },
    controls: getNodeCfg => ({
        workInstructionVersionId: {
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.workInstructionVersionId,
            cfg: {
                kind: 'selectWorkInstructionVersion',
                getValue: n => n.kind === 'workInstruction' ? n.controlValues.workInstructionVersionId : null,
                onSelect: async workInstructionVersionId => {
                    const nodeCfg = getNodeCfg();
                    const {
                        argo_work_instruction_version_by_pk,
                    } = await gqlClient.request({
                        document: GetWorkInstructionVersionDetailDocument,
                        variables: {
                            workInstructionVersionId,
                        },
                    });
                    if (!argo_work_instruction_version_by_pk) {
                        Notify.error(`Work instruction version with ID ${workInstructionVersionId} not found.`);
                        return;
                    }
                    const configParseResult = workInstructionVersionSchema.safeParse(argo_work_instruction_version_by_pk.config);
                    if (!configParseResult.success) {
                        const zodError = fromZodError(configParseResult.error);
                        const err = CausalError.from({
                            message: 'Encountered an error while attempting to parse the config',
                            cause: zodError.message, // including the whole zod error causes too much noise, so we'll include it alongside it
                            zodError,
                        });
                        Notify.error(err);
                        return;
                    }

                    const workInstructionVersionConfig = configParseResult.data;

                    const processIds = workInstructionVersionConfig.steps.map(s => s.linkedProcessIds).flat();

                    if (!processIds.length) {
                        const newNodeCfg: NodeCfg<'workInstruction'> = {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                workInstructionVersionId,
                                workInstructionDetail: {
                                    kind: 'default',
                                    workInstructionOutputs: workInstructionVersionConfig.outputs,
                                },
                            },
                        };
                        createNodeSocket({
                            newNodeCfg,
                        });
                        return;
                    }
                    const processDataElements = await gqlClient.request({
                        document: GetProcessDataElementsForWorkInstructionInputsDocument,
                        variables: {
                            processIds,
                        },
                    });

                    const steps: ProcessWorkInstruction['steps'] = [];

                    for (const step of workInstructionVersionConfig.steps) {
                        const stepProcessData: ProcessWorkInstruction['steps'][number] = {
                            processes: [],
                            name: step.name,
                        };
                        let remainingStepProcessIds = step.linkedProcessIds;
                        if (nodeCfg.controlValues.workInstructionDetail.kind === 'process') {
                            const existingStepProcessData = nodeCfg.controlValues.workInstructionDetail.steps.find(s => s.name === step.name);
                            if (existingStepProcessData) {
                                for (const existingProcess of existingStepProcessData.processes) {
                                    // if all of the following are true, reuse the existing process collection config to avoid making the user re-configure it
                                    // 1. Existing work instruction node has process related stuff
                                    // 2. The existing work instruction has a step with the same name as a step in the incoming work instruction
                                    // 3. The existing work instruction step has process related stuff
                                    // 4. The process in the step matches the process in the incoming step
                                    if (remainingStepProcessIds.includes(existingProcess.id)) {
                                        stepProcessData.processes.push(existingProcess);
                                        remainingStepProcessIds = remainingStepProcessIds.filter(i => i !== existingProcess.id);
                                    }
                                }
                            }
                        }
                        for (const processId of remainingStepProcessIds) {
                            const processData = processDataElements.process_process.filter(p => p.id === processId);
                            for (const process of processData) {
                                const processItem: ProcessWorkInstruction['steps'][number]['processes'][number] = {
                                    id: process.id,
                                    name: process.name,
                                    params: process.data_elements.filter(de => de.collection_kind === ProcessDataElementCollectionKindEnum.SpecParam).map(de => {
                                        const deKind = de.data_element_kind.kind as 'string' | 'number' | 'pass';
                                        if (deKind === 'pass') {
                                            return {
                                                id: de.id,
                                                name: de.name,
                                                kind: 'number',
                                            };
                                        }
                                        return {
                                            id: de.id,
                                            kind: deKind,
                                            name: de.name,
                                        };
                                    }),
                                    collectTrigger: {
                                        eventTrigger: '',
                                        evalTrigger: 'event',
                                    },
                                    dataElements: process.data_elements.filter(de => de.collection_kind === ProcessDataElementCollectionKindEnum.Collect).map(de => ({
                                        id: de.id,
                                        name: de.name,
                                        sourceVar: '',
                                    })),
                                };
                                stepProcessData.processes.push(processItem);
                            }
                        }
                        steps.push(stepProcessData);
                    }

                    const newNodeCfg: NodeCfg<'workInstruction'> = {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            workInstructionVersionId,
                            location: nodeCfg.controlValues.location || {
                                id: '',
                            },
                            workInstructionDetail: {
                                steps,
                                kind: 'process',
                                workInstructionOutputs: workInstructionVersionConfig.outputs,
                            },
                        },
                    };

                    createNodeSocket({
                        newNodeCfg,
                    });
                },
            },
        },
        location: {
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.location,
            cfg: {
                kind: 'location',
                onClear: () => {
                    const nodeCfg = getNodeCfg();
                    const newNodeCfg: NodeCfg<'workInstruction'> = {
                        ...nodeCfg,
                        controlValues: {
                            // omitting the location property to unset it
                            workInstructionDetail: nodeCfg.controlValues.workInstructionDetail,
                            workInstructionVersionId: nodeCfg.controlValues.workInstructionVersionId,
                        },
                    };
                    createNodeSocket({ newNodeCfg });
                },
            },
        },
    }),
    cfg: () => {
        const workInstructionNodeCfg: NodeCfg<'workInstruction'> = {
            disabled: false,
            id: getUID(),
            kind: 'workInstruction',
            controlValues: {
                workInstructionVersionId: '',
                workInstructionDetail: {
                    kind: 'default',
                    workInstructionOutputs: {},
                },
            },
        };
        return workInstructionNodeCfg;
    },
    inputs: _cfg => ({
        activate: { socket: 'event', label: 'Activate' },
        materialId: { socket: 'string', label: 'Material ID' },
    }),
    outputs: nodeCfg => {
        const controlOutputs: {
            [k in keyof NodeControlSocketKindMap['workInstruction']['outputs']]: UiFlowNodeOutput.Opts<NodeControlSocketKindMap['workInstruction']['outputs'][k]>;
        } = {
            started: { socket: 'event', label: 'Started' },
            completed: { socket: 'event', label: 'Completed' },
            stepUpdate: { socket: 'event', label: 'Step Update' },
        };
        const dataOutputs: {
            [k in keyof NodeDataSocketKindMap['workInstruction']['outputs']]: UiFlowNodeOutput.Opts<NodeDataSocketKindMap['workInstruction']['outputs'][k]>;
        } = {
            totalStepNum: { socket: 'number', label: 'Total Number of Steps' },
            currentStepNum: { socket: 'number', label: 'Current Step Number' },
            workInstructionName: { socket: 'string', label: 'Work Instruction Name' },
            workInstructionVersion: { socket: 'number', label: 'Work Instruction Version' },
        };
        for (const [ workInstructionOutputId, outputData ] of Object.entries(nodeCfg.controlValues.workInstructionDetail.workInstructionOutputs).sort(([ , v1 ], [ , v2 ]) => v1.idx < v2.idx ? -1 : 1)) {
            let nodeSocketKind: NodeSocketKind;
            if (typeof outputData.defaultValue === 'number') {
                nodeSocketKind = 'number';
            } else if (typeof outputData.defaultValue === 'boolean') {
                nodeSocketKind = 'boolean';
            } else if (typeof outputData.defaultValue === 'string') {
                nodeSocketKind = 'string';
            } else {
                throw new Error(`Unknown node socket kind: ${JSON.stringify(typeof outputData.defaultValue)}`);
            }
            dataOutputs[workInstructionOutputId] = { socket: nodeSocketKind, label: outputData.name };
        }
        if (nodeCfg.controlValues.workInstructionDetail.kind === 'process') {
            for (const step of nodeCfg.controlValues.workInstructionDetail.steps) {
                for (const process of step.processes) {
                    for (const param of process.params) {
                        dataOutputs[param.id] = { socket: param.kind, label: `${step.name} - ${process.name} - ${param.name}` };
                    }
                }
            }
        }
        return {
            ...controlOutputs,
            ...dataOutputs,
        } as ReturnType<UiNodeSpec<'workInstruction'>['outputs']>;
    },
    validator: async node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.workInstructionVersionId) {
            errors.workInstructionVersionId = 'Work Instruction ID is required';
            // returning early to avoid running queries that will fail
            return errors;
        }

        const {
            argo_work_instruction_version_by_pk,
        } = await gqlClient.request({
            document: GetWorkInstructionVersionDetailDocument,
            variables: {
                workInstructionVersionId: node.controlValues.workInstructionVersionId,
            },
        });
        if (!argo_work_instruction_version_by_pk) {
            throw new Error(`Work instruction version with ID ${node.controlValues.workInstructionVersionId} not found.`);
        }

        const workInstructionVersionStatus = argo_work_instruction_version_by_pk.status;
        if (workInstructionVersionStatus !== ArgoWorkInstructionVersionStatusEnum.Released) {
            if (workInstructionVersionStatus === ArgoWorkInstructionVersionStatusEnum.Retired) {
                if (!argo_work_instruction_version_by_pk.discontinue_after) {
                    errors.workInstructionVersionId = 'Work instruction version is retired and must have a discontinue_after date.';
                } else if (new Date() >= new Date(argo_work_instruction_version_by_pk.discontinue_after)) {
                    errors.workInstructionVersionId = 'Work instruction version is retired and must not be used after discontinue_after date.';
                }
            } else {
                errors.workInstructionVersionId = `Work instruction version is not released, current status: ${workInstructionVersionStatus}`;
            }
        }

        if (node.controlValues.workInstructionDetail.kind === 'process') {

            if (!node.controlValues.location ||
                ('deployParamKind' in node.controlValues.location && !node.controlValues.location.deployParamId) ||
                ('id' in node.controlValues.location && !node.controlValues.location.id)
            ) {
                errors.location = 'Location is required';
            }

            const allVariables = accessor.appletDesign.allAppletVariables;

            for (const step of node.controlValues.workInstructionDetail.steps) {
                for (const process of step.processes) {
                    for (const dataElement of process.dataElements) {
                        if (!dataElement.sourceVar) {
                            errors[`dataInput_${dataElement.id}`] = `Data input for ${step.name} - ${process.name} - ${dataElement.name} is required`;
                        } else if (!allVariables[dataElement.sourceVar]) {
                            errors[`dataInput_${dataElement.id}`] = `Data input for ${step.name} - ${process.name} - ${dataElement.name} is not found`;
                        }
                    }
                }
            }
        }

        return Object.keys(errors).length === 0 ? true : errors;
    },
};
